import ReactDOM from "react-dom/client";
import { BrowserRouter, useNavigate, useRoutes } from "react-router";
import { ThemeProvider } from "@mui/material/styles";
import { Modal, styled } from "@mui/material";
import { SnackbarProvider } from "notistack";
import CssBaseline from "@mui/material/CssBaseline";

import { getRoutes } from "./Router/router";
import { ModalContext, useModal } from "./Components/Modals";
import { TutorialContext } from "./Utils/Helpers/tutorial";
import useTutorial from "./Utils/Hooks/useTutorial";
import { ThemeLight, ThemeDark } from "./Theme";

import { UserThemeContext } from "./Utils/Helpers/userTheme";
import useUserTheme from "./Utils/Hooks/useUserTheme";

import { ReactSession } from "react-client-session";
import { AxiosInterceptor, cacheService } from "./Core/Api/services";

import { LicenseInfo } from "@mui/x-license";
import { Provider } from "react-redux";
import { reduxStore } from "./Redux/redux-store";
import React, { useEffect } from "react";

import useZendeskScript from "./Utils/Hooks/useZendesk";
import { pdfjs } from "react-pdf";
import moment from "moment";
import "moment/locale/fr";
import "./i18n";

// MUI licence
LicenseInfo.setLicenseKey(
  "f78f58c1b8eefb8ca0286edf02eacfbfTz0xMDE3NDksRT0xNzYzMTI5NTQzMDAwLFM9cHJlbWl1bSxMTT1zdWJzY3JpcHRpb24sUFY9aW5pdGlhbCxLVj0y"
);

// React session stored on local storage
ReactSession.setStoreType("localStorage");

function App() {
  let element = useRoutes(getRoutes());
  const [modalElement, isModalOpen, openModal, closeModal] = useModal();
  const { ...tutorialProviderValues } = useTutorial();
  const { userTheme, setUserTheme } = useUserTheme();
  const appInfos = cacheService.GetPlatformSettings();
  const navigate = useNavigate();

  // Set path to local for pdf preview.
  pdfjs.GlobalWorkerOptions.workerSrc = `/pdfjs-dist-3.11.174/pdf.worker.min.js`;

  // Include user guiding (not used anymore)
  // useUserguidingScript();

  // Include zendesk
  useZendeskScript();

  const [theme, setTheme] = React.useState<any>(ThemeDark);

  // Theme
  useEffect(() => {
    setTheme(userTheme === "light" ? ThemeLight : ThemeDark);
    console.debug("App::theme", userTheme);
  }, [userTheme]);

  // Change dynamically page title
  React.useEffect(() => {
    const brandName = appInfos?.brandName ?? "mediaspot.io";
    if (appInfos?.name) document.title = `${brandName} | ${appInfos.name}`;
  }, [appInfos]);

  // TEMP handle old angular routes
  React.useEffect(() => {
    if (window.location.hash.startsWith("#/")) {
      navigate(window.location.hash.substring(2));
      console.info("router:: redirect from legacy ng url = " + window.location.hash);
    }
  }, [window.location]);

  // Change dynamically favicon
  React.useEffect(() => {
    const language = localStorage.getItem("i18nextLng") || "en";
    moment.locale(language);
    const favicon = document.getElementById("favicon") as HTMLLinkElement;
    const apple_favicon = document.getElementById("apple_favicon") as HTMLLinkElement;
    const brandName = document.getElementById("brand_name") as HTMLLinkElement;

    const platformBrandName = cacheService.GetPlatformSettings()?.brandName as string;
    const platformFaviconUrl = cacheService.GetPlatformSettings()?.faviconUrl as string;

    if (brandName !== null && platformBrandName !== null) brandName.textContent = platformBrandName;
    if (platformFaviconUrl !== null && favicon !== null) favicon.href = platformFaviconUrl;
    if (platformFaviconUrl !== null && apple_favicon !== null) apple_favicon.href = platformFaviconUrl;
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Provider store={reduxStore}>
        <UserThemeContext.Provider value={{ userTheme, setUserTheme }}>
          <SnackbarProvider>
            <TutorialContext.Provider value={tutorialProviderValues}>
              <ModalContext.Provider value={{ openModal, closeModal }}>
                <AxiosInterceptor>
                  <StyledModal open={isModalOpen} onClose={closeModal}>
                    {modalElement || <></>}
                  </StyledModal>
                  {element}
                </AxiosInterceptor>
              </ModalContext.Provider>
            </TutorialContext.Provider>
          </SnackbarProvider>
        </UserThemeContext.Provider>
      </Provider>
    </ThemeProvider>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

root.render(
  <BrowserRouter>
    <App />
  </BrowserRouter>
);

const StyledModal = styled(Modal)(() => ({
  position: "absolute",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",

  ".MuiBackdrop-root": {
    position: "absolute",
  },
}));
